import { Icon } from "@/components/Icon";
import type { QueuedEntry } from "@/offline/outboxStore";
import "./OutboxStatus.css";

interface OutboxStatusProps {
  items: QueuedEntry[];
}

/**
 * Visible, honest state for entries accepted while offline (of-002). The
 * task is explicit that the visitor must never see an ERROR for an entry
 * the outbox already accepted — this banner is what replaces that error,
 * not a decoration next to one.
 *
 * `role="status"` (a polite live region), never `role="alert"` — nothing
 * failed, an entry is just waiting its turn. `data-state="pending"` is the
 * breadcrumb a tester reads instead of inferring "queued" from the
 * wifi-off icon alone (`rules/ui-breadcrumbs.md` #1).
 */
export function OutboxStatus({ items }: OutboxStatusProps) {
  if (items.length === 0) return null;

  return (
    <div className="outbox-status" role="status" data-state="pending">
      <span className="outbox-status__icon" aria-hidden="true">
        <Icon name="wifi-off" size={16} />
      </span>
      <span className="outbox-status__text">
        {items.length} {items.length === 1 ? "entry" : "entries"} pending — will sync
        automatically as soon as we can send {items.length === 1 ? "it" : "them"}.
      </span>
    </div>
  );
}
